import { useMemo, useState } from "react";
import { clsx } from "clsx";
import { ArrowDown, ArrowUp, Download, Route } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { EmptyState, SubTabs, TableShell, Th } from "@/features/power_projects/components/ui";
import { TITLE_STATUS_LABEL, type LedOwner, type LedParcel, type TitleSummary } from "../types";
import { INPUT_SM } from "./common";

/** Stan tytułów działki — słownictwo DLC 01, żeby plakietka znaczyła to samo, co stan
 *  przy właścicielu w karcie działki. */
const SUMMARY: Record<TitleSummary, { color: "green" | "yellow" | "red" | "gray"; label: string }> = {
  secured: { color: "green", label: "wszystkie pozyskane" },
  in_progress: { color: "yellow", label: "w trakcie" },
  problem: { color: "red", label: "problem / odmowa" },
  not_started: { color: "gray", label: "nie rozpoczęto" },
};

type Filter = "route" | "all" | "todo" | "problem" | "public" | "off_route";
type SortKey = "teryt" | "obreb" | "length" | "owners" | "titles";
/** Ta sama lista w trzech ujęciach: działka (jeden wiersz = jedna działka), para
 *  działka-strona (jeden wiersz = jedna osoba na jednej działce) i strona zbiorczo
 *  (jeden wiersz = osoba z wszystkimi swoimi działkami). */
type View = "parcels" | "pairs" | "owners";

interface PairRow {
  key: string;
  parcel: LedParcel;
  owner: LedOwner;
}

interface OwnerRow {
  key: string;
  owner: LedOwner;
  parcels: LedParcel[];
  length_m: number;
  secured: number;
}

const TITLE_TONE: Record<string, "green" | "yellow" | "red" | "gray"> = {
  secured: "green",
  not_required: "green",
  in_progress: "yellow",
  problem: "red",
  refused: "red",
  not_started: "gray",
};

/** Tytuł liczy się jako pozyskany tylko NA BIEŻĄCEJ wersji trasy — tak jak status działki w
 *  DLC 01. Pozyskany dla starej wersji (title_stale) to jeszcze nie uzgodnienie. */
const ownerSecured = (o: LedOwner) =>
  (o.title_status === "secured" && !o.title_stale) || o.title_status === "not_required";

/** Podpis tytułu nieaktualnego: dla której wersji go pozyskano (albo że bez wersji trasy). */
export function staleTitleLabel(owner: Pick<LedOwner, "title_route_version">): string {
  return owner.title_route_version ? `pozyskany · stara trasa (${owner.title_route_version})` : "pozyskany · bez wersji trasy";
}

/** Plakietka stanu tytułu przy stronie: tytuł na starej trasie dostaje własny kolor i podpis. */
function TitleBadge({ owner }: { owner: LedOwner }) {
  if (owner.title_status === "secured" && owner.title_stale) {
    return <Badge color="yellow">{staleTitleLabel(owner)}</Badge>;
  }
  return <Badge color={TITLE_TONE[owner.title_status] ?? "gray"}>{TITLE_STATUS_LABEL[owner.title_status]}</Badge>;
}

function pairRows(parcels: LedParcel[]): PairRow[] {
  return parcels.flatMap((p) => p.owners.map((o) => ({ key: `${p.id}:${o.id}`, parcel: p, owner: o })));
}

/** Ta sama osoba potrafi mieć kilka działek — zbieramy po nazwisku i numerze PESEL/NIP,
 *  bo w EGiB ten sam właściciel jest osobnym rekordem przy każdej działce. */
function ownerRows(parcels: LedParcel[]): OwnerRow[] {
  const map = new Map<string, OwnerRow>();
  for (const p of parcels) {
    for (const o of p.owners) {
      const key = `${o.full_name.toLowerCase()}|${o.pesel_nip || ""}`;
      const row = map.get(key) ?? { key, owner: o, parcels: [], length_m: 0, secured: 0 };
      row.parcels.push(p);
      row.length_m += p.intersection_length_m ?? 0;
      if (ownerSecured(o)) row.secured += 1;
      map.set(key, row);
    }
  }
  return [...map.values()];
}

interface Props {
  parcels: LedParcel[];
  projectName: string;
  onOpen: (parcelId: string) => void;
}

const securedCount = (p: LedParcel) => p.owners.filter(ownerSecured).length;

/** Działka uzgodniona w warsztacie ZE (status „ok": ręcznie, tryb „nie wymaga", droga >50%
 *  udziałów albo komplet tytułów) — pozyskana niezależnie od tytułów stron. */
const agreedByStatus = (p: LedParcel) => p.status === "ok";

/** Działka „z kompletem": uzgodniona statusem albo ma strony i KAŻDA ma pozyskany
 *  (albo niewymagany) tytuł. */
const isDone = (p: LedParcel) => agreedByStatus(p) || (p.owners.length > 0 && securedCount(p) === p.owners.length);

/** Etykieta plakietki tytułów: uzgodnienie zapisane statusem (bez kompletu tytułów) mówi
 *  to wprost, żeby „wszystkie pozyskane" obok „0/2" nie wyglądało na błąd. */
function summaryLabel(p: LedParcel): string {
  if (agreedByStatus(p) && securedCount(p) < p.owners.length) {
    return p.status_locked ? "uzgodniona (status ręczny)" : "uzgodniona (status działki)";
  }
  return (SUMMARY[p.title_summary] ?? SUMMARY.not_started).label;
}

function matches(p: LedParcel, filter: Filter): boolean {
  switch (filter) {
    case "all":
      return true;
    case "route":
      return p.in_current_route;
    case "off_route":
      return !p.in_current_route;
    case "public":
      return p.is_public;
    case "problem":
      return p.title_summary === "problem";
    case "todo":
      return p.in_current_route && !p.is_public && !isDone(p);
  }
}

function compare(a: LedParcel, b: LedParcel, key: SortKey): number {
  switch (key) {
    case "teryt":
      return a.teryt.localeCompare(b.teryt, "pl", { numeric: true });
    case "obreb":
      return (a.obreb || "").localeCompare(b.obreb || "", "pl");
    case "length":
      return (a.intersection_length_m ?? -1) - (b.intersection_length_m ?? -1);
    case "owners":
      return a.owners.length - b.owners.length;
    case "titles":
      return (
        securedCount(a) / Math.max(1, a.owners.length) - securedCount(b) / Math.max(1, b.owners.length)
      );
  }
}

/** Eksport tego, co widać — te same kolumny i ta sama kolejność co w tabeli.
 *  Średnik i BOM, żeby Excel po polsku otworzył plik bez czarów z importem. */
function saveCsv(head: string[], lines: string[][], projectName: string, suffix: string): void {
  const esc = (v: string) => (/[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const csv = [head, ...lines].map((r) => r.map(esc).join(";")).join("\r\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${suffix}-${projectName.replace(/[^\w.-]+/g, "_").toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadPairsCsv(rows: PairRow[], projectName: string): void {
  saveCsv(
    ["Działka (TERYT)", "Obręb", "Strona", "Rodzaj strony", "Udział", "Stan tytułu", "Podstawa", "KW", "Metry w trasie"],
    rows.map((r) => [
      r.parcel.teryt,
      r.parcel.obreb ?? "",
      r.owner.full_name,
      r.owner.kind === "person" ? "osoba" : "instytucja / firma",
      r.owner.share ?? "",
      r.owner.title_stale ? staleTitleLabel(r.owner) : r.owner.title_status_display,
      r.owner.title_basis ?? "",
      r.parcel.kw_number ?? "",
      r.parcel.intersection_length_m != null ? String(Math.round(r.parcel.intersection_length_m)) : "",
    ]),
    projectName,
    "dzialki-strony",
  );
}

function downloadOwnersCsv(rows: OwnerRow[], projectName: string): void {
  saveCsv(
    ["Strona", "Rodzaj", "Działek", "Metry w trasie", "Tytuły pozyskane", "Stan ostatni", "Kontakt", "Działki"],
    rows.map((r) => [
      r.owner.full_name,
      r.owner.kind === "person" ? "osoba" : "instytucja / firma",
      String(r.parcels.length),
      String(Math.round(r.length_m)),
      `${r.secured}/${r.parcels.length}`,
      r.owner.title_status_display,
      [r.owner.phone, r.owner.email].filter(Boolean).join(" / "),
      r.parcels.map((p) => p.teryt).join(" | "),
    ]),
    projectName,
    "strony",
  );
}

function downloadCsv(rows: LedParcel[], projectName: string): void {
  const head = ["Działka (TERYT)", "Obręb", "Gmina", "Rodzaj", "W trasie", "Metry w trasie", "KW", "Strony", "Tytuły pozyskane", "Stan"];
  const lines = rows.map((p) => [
    p.teryt,
    p.obreb ?? "",
    p.commune ?? "",
    p.is_public ? "publiczna" : "prywatna",
    p.in_current_route ? "tak" : "nie",
    p.intersection_length_m != null ? String(Math.round(p.intersection_length_m)) : "",
    p.kw_number ?? "",
    p.owners.map((o) => o.full_name).join(" | "),
    `${securedCount(p)}/${p.owners.length}`,
    summaryLabel(p),
  ]);
  const esc = (v: string) => (/[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const csv = [head, ...lines].map((r) => r.map(esc).join(";")).join("\r\n");
  const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `dzialki-${projectName.replace(/[^\w.-]+/g, "_").toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function SortTh({
  label,
  sortKey,
  active,
  dir,
  onSort,
  className,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey;
  dir: 1 | -1;
  onSort: (k: SortKey) => void;
  className?: string;
}) {
  const on = active === sortKey;
  return (
    <Th className={className}>
      <button
        type="button"
        className={clsx("inline-flex items-center gap-1 hover:text-[var(--color-primary)]", on && "text-[var(--color-primary)]")}
        onClick={() => onSort(sortKey)}
        title="Sortuj"
      >
        {label}
        {on && (dir === 1 ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
      </button>
    </Th>
  );
}

/** Działki i strony: jedna lista (prywatne, publiczne, poza trasą), pełne numery TERYT.
 *  Klik w wiersz otwiera kartę działki — jedno działanie, bez rozwijania. */
export function ParcelsTable({ parcels, projectName, onOpen }: Props) {
  const [filter, setFilter] = useState<Filter>("route");
  const [view, setView] = useState<View>("parcels");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<SortKey>("teryt");
  const [dir, setDir] = useState<1 | -1>(1);

  const counts = useMemo(
    () => ({
      all: parcels.length,
      route: parcels.filter((p) => matches(p, "route")).length,
      off_route: parcels.filter((p) => matches(p, "off_route")).length,
      public: parcels.filter((p) => matches(p, "public")).length,
      problem: parcels.filter((p) => matches(p, "problem")).length,
      todo: parcels.filter((p) => matches(p, "todo")).length,
    }),
    [parcels],
  );

  const needle = q.trim().toLowerCase();
  const visible = useMemo(() => {
    const rows = parcels
      .filter((p) => matches(p, filter))
      .filter(
        (p) =>
          !needle ||
          p.teryt.toLowerCase().includes(needle) ||
          (p.kw_number ?? "").toLowerCase().includes(needle) ||
          (p.obreb ?? "").toLowerCase().includes(needle) ||
          p.owners.some((o) => o.full_name.toLowerCase().includes(needle)),
      );
    return rows.sort((a, b) => compare(a, b, sort) * dir);
  }, [parcels, filter, needle, sort, dir]);

  const pairs = useMemo(() => {
    const rows = pairRows(visible).filter(
      (r) => !needle || r.parcel.teryt.toLowerCase().includes(needle) || r.owner.full_name.toLowerCase().includes(needle),
    );
    return rows.sort((a, b) =>
      sort === "titles" || sort === "owners"
        ? a.owner.full_name.localeCompare(b.owner.full_name, "pl") * dir
        : compare(a.parcel, b.parcel, sort) * dir,
    );
  }, [visible, needle, sort, dir]);

  const owners = useMemo(() => {
    const rows = ownerRows(visible).filter((r) => !needle || r.owner.full_name.toLowerCase().includes(needle));
    return rows.sort((a, b) => {
      if (sort === "length") return (a.length_m - b.length_m) * dir;
      if (sort === "owners") return (a.parcels.length - b.parcels.length) * dir;
      if (sort === "titles") return (a.secured / a.parcels.length - b.secured / b.parcels.length) * dir;
      return a.owner.full_name.localeCompare(b.owner.full_name, "pl") * dir;
    });
  }, [visible, needle, sort, dir]);

  const rowCount = view === "parcels" ? visible.length : view === "pairs" ? pairs.length : owners.length;
  const exportCsv = () => {
    if (view === "parcels") return downloadCsv(visible, projectName);
    if (view === "pairs") return downloadPairsCsv(pairs, projectName);
    return downloadOwnersCsv(owners, projectName);
  };

  const onSort = (k: SortKey) => {
    if (k === sort) setDir((d) => (d === 1 ? -1 : 1));
    else {
      setSort(k);
      setDir(1);
    }
  };

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <SubTabs<Filter>
          active={filter}
          onChange={setFilter}
          tabs={[
            { key: "route", label: "W trasie", count: counts.route || undefined, countTone: "blue" },
            { key: "todo", label: "Bez kompletu tytułów", count: counts.todo || undefined, countTone: "warn" },
            { key: "problem", label: "Problem / odmowa", count: counts.problem || undefined, countTone: "danger" },
            { key: "public", label: "Publiczne", count: counts.public || undefined },
            { key: "off_route", label: "Poza trasą", count: counts.off_route || undefined },
            { key: "all", label: "Wszystkie", count: counts.all || undefined },
          ]}
        />
        <span className="flex flex-wrap items-center gap-2">
          <span className="inline-flex" role="group" aria-label="Widok listy">
            {(
              [
                ["parcels", "działki"],
                ["pairs", "działka + strona"],
                ["owners", "strony zbiorczo"],
              ] as [View, string][]
            ).map(([key, label], i) => (
              <button
                key={key}
                type="button"
                onClick={() => setView(key)}
                className={clsx(
                  "border px-2 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors",
                  i > 0 && "-ml-px",
                  view === key
                    ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                    : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]",
                )}
              >
                {label}
              </button>
            ))}
          </span>
          <input
            className={clsx(INPUT_SM, "w-64")}
            placeholder="Numer działki, KW, obręb albo nazwisko"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Button
            size="sm"
            variant="secondary"
            disabled={rowCount === 0}
            title="Pobierz widoczne wiersze do CSV (Excel)"
            onClick={exportCsv}
          >
            <Download size={14} /> CSV ({rowCount})
          </Button>
        </span>
      </div>
      {parcels.length === 0 ? (
        <div className="card"><EmptyState>Brak działek — trasa nie została jeszcze wgrana</EmptyState></div>
      ) : view === "pairs" ? (
        <TableShell tableClassName="text-[13px]">
          <thead>
            <tr>
              <SortTh label="Działka" sortKey="teryt" active={sort} dir={dir} onSort={onSort} />
              <Th>Obręb</Th>
              <SortTh label="Strona" sortKey="owners" active={sort} dir={dir} onSort={onSort} />
              <Th>Udział</Th>
              <Th>Stan tytułu</Th>
              <Th>KW</Th>
              <SortTh label="m w trasie" sortKey="length" active={sort} dir={dir} onSort={onSort} className="text-right" />
            </tr>
          </thead>
          <tbody>
            {pairs.map((r) => (
              <tr
                key={r.key}
                tabIndex={0}
                role="button"
                title="Otwórz kartę działki"
                className={clsx(
                  "cursor-pointer border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)] focus-visible:bg-[var(--color-bg-elevated)] focus-visible:outline-none",
                  !r.parcel.in_current_route && "text-[var(--color-text-muted)]",
                )}
                onClick={() => onOpen(r.parcel.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen(r.parcel.id);
                  }
                }}
              >
                <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-[var(--color-text-primary)]">{r.parcel.teryt}</td>
                <td className="px-3 py-2">{r.parcel.obreb}</td>
                <td className="px-3 py-2">
                  {r.owner.full_name}
                  {r.owner.kind !== "person" && (
                    <span className="ml-1.5 font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">instytucja</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-[var(--color-text-secondary)]">{r.owner.share || "—"}</td>
                <td className="whitespace-nowrap px-3 py-2">
                  <TitleBadge owner={r.owner} />
                  {r.owner.title_basis && (
                    <span className="ml-1.5 text-[11px] text-[var(--color-text-muted)]">{r.owner.title_basis}</span>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{r.parcel.kw_number || "—"}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">
                  {r.parcel.intersection_length_m != null ? Math.round(r.parcel.intersection_length_m) : "—"}
                </td>
              </tr>
            ))}
            {pairs.length === 0 && (
              <tr><td colSpan={7}><EmptyState>Nic nie pasuje do filtra</EmptyState></td></tr>
            )}
          </tbody>
        </TableShell>
      ) : view === "owners" ? (
        <TableShell tableClassName="text-[13px]">
          <thead>
            <tr>
              <SortTh label="Strona" sortKey="teryt" active={sort} dir={dir} onSort={onSort} />
              <SortTh label="Działek" sortKey="owners" active={sort} dir={dir} onSort={onSort} className="text-right" />
              <Th>Działki</Th>
              <SortTh label="m w trasie" sortKey="length" active={sort} dir={dir} onSort={onSort} className="text-right" />
              <SortTh label="Tytuły" sortKey="titles" active={sort} dir={dir} onSort={onSort} />
              <Th>Kontakt</Th>
            </tr>
          </thead>
          <tbody>
            {owners.map((r) => (
              <tr key={r.key} className="border-t border-[var(--color-border-muted)] align-top">
                <td className="px-3 py-2">
                  <span className="font-medium">{r.owner.full_name}</span>
                  {r.owner.kind !== "person" && (
                    <span className="ml-1.5 font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">instytucja</span>
                  )}
                  {r.owner.address && (
                    <span className="block text-[11px] text-[var(--color-text-muted)]">{r.owner.address}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{r.parcels.length}</td>
                <td className="px-3 py-2">
                  <span className="flex flex-wrap gap-1">
                    {r.parcels.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className="border border-[var(--color-border)] px-1.5 py-px font-mono text-[10px] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
                        onClick={() => onOpen(p.id)}
                        title="Otwórz kartę działki"
                      >
                        {p.short_number || p.teryt}
                      </button>
                    ))}
                  </span>
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{Math.round(r.length_m)}</td>
                <td className="whitespace-nowrap px-3 py-2">
                  <Badge color={r.secured === r.parcels.length ? "green" : r.secured > 0 ? "yellow" : "gray"}>
                    {r.secured}/{r.parcels.length}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-[12px] text-[var(--color-text-secondary)]">
                  {[r.owner.phone, r.owner.email].filter(Boolean).join(" · ") || "—"}
                </td>
              </tr>
            ))}
            {owners.length === 0 && (
              <tr><td colSpan={6}><EmptyState>Nic nie pasuje do filtra</EmptyState></td></tr>
            )}
          </tbody>
        </TableShell>
      ) : (
        <TableShell tableClassName="text-[13px]">
          <thead>
            <tr>
              <SortTh label="Działka" sortKey="teryt" active={sort} dir={dir} onSort={onSort} />
              <SortTh label="Obręb / gmina" sortKey="obreb" active={sort} dir={dir} onSort={onSort} />
              <Th>Rodzaj</Th>
              <SortTh label="m w trasie" sortKey="length" active={sort} dir={dir} onSort={onSort} className="text-right" />
              <Th>KW</Th>
              <SortTh label="Strony" sortKey="owners" active={sort} dir={dir} onSort={onSort} />
              <SortTh label="Tytuły prawne" sortKey="titles" active={sort} dir={dir} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => {
              const secured = securedCount(p);
              const s = SUMMARY[p.title_summary] ?? SUMMARY.not_started;
              return (
                <tr
                  key={p.id}
                  tabIndex={0}
                  role="button"
                  title="Otwórz kartę działki"
                  className={clsx(
                    "cursor-pointer border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)] focus-visible:bg-[var(--color-bg-elevated)] focus-visible:outline-none",
                    !p.in_current_route && "text-[var(--color-text-muted)]",
                  )}
                  onClick={() => onOpen(p.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onOpen(p.id);
                    }
                  }}
                >
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-[var(--color-text-primary)]">
                    {p.teryt}
                    {!p.in_current_route && (
                      <span className="ml-1.5 font-sans text-[10px] uppercase tracking-wider">poza trasą</span>
                    )}
                    {p.route_change && (
                      <span
                        className="ml-1.5 inline-flex items-center gap-0.5 border border-amber-500 px-1 py-px font-sans text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-400"
                        title={p.status_reason || "Zmiana trasy na działce"}
                        data-testid="route-change-chip"
                      >
                        <Route size={10} /> zmiana trasy
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {p.obreb}
                    {p.commune && <span className="text-[var(--color-text-muted)]">, {p.commune}</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">{p.is_public ? "publiczna" : "prywatna"}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {p.intersection_length_m != null ? Math.round(p.intersection_length_m) : "—"}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{p.kw_number || "—"}</td>
                  <td className="px-3 py-2">
                    {p.owners.length === 0 ? (
                      <span className="text-[var(--color-text-muted)]">brak stron</span>
                    ) : (
                      <>
                        {p.owners.slice(0, 2).map((o) => o.full_name).join(", ")}
                        {p.owners.length > 2 && (
                          <span className="text-[var(--color-text-muted)]"> +{p.owners.length - 2}</span>
                        )}
                      </>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {p.owners.length > 0 && (
                      <span className="inline-flex items-center gap-1.5" title={p.status_reason || undefined}>
                        <Badge color={s.color}>{summaryLabel(p)}</Badge>
                        <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
                          {secured}/{p.owners.length}
                        </span>
                        {(p.route_change?.stale_titles.length ?? 0) > 0 && (
                          <span className="font-sans text-[10px] text-amber-700 dark:text-amber-400">
                            {p.route_change!.stale_titles.length === 1 ? "1 tytuł na starej trasie" : `${p.route_change!.stale_titles.length} tytuły na starej trasie`}
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {visible.length === 0 && (
              <tr>
                <td colSpan={7}><EmptyState>Nic nie pasuje do filtra</EmptyState></td>
              </tr>
            )}
          </tbody>
        </TableShell>
      )}
    </div>
  );
}
